import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import type {
  PerformanceGrain,
  PerformanceQuery,
  PerformanceRole
} from "./types"

const GRAINS: PerformanceGrain[] = ["month", "quarter", "year"]
const ROLES: PerformanceRole[] = ["all", "project_manager", "sales_pm"]

export interface PerformanceSort {
  key: string
  dir: "ascend" | "descend"
}

export interface PerformanceUrlPatch {
  grain?: PerformanceGrain
  period?: string | null
  role?: PerformanceRole
  projectType?: string | null
  sort?: PerformanceSort | null
}

const pick = <T extends string>(
  value: string | null,
  allowed: T[],
  fallback: T
): T => (allowed.includes(value as T) ? (value as T) : fallback)

// Every filter lives in the URL, so Back restores the previous view and a
// shared link reproduces it (spec §3 "State persistence").
export function usePerformanceUrlState() {
  const [searchParams, setSearchParams] = useSearchParams()

  const query: PerformanceQuery = useMemo(
    () => ({
      grain: pick(searchParams.get("grain"), GRAINS, "month"),
      period: searchParams.get("period") ?? undefined,
      role: pick(searchParams.get("role"), ROLES, "all"),
      projectType: searchParams.get("type") ?? undefined
    }),
    [searchParams]
  )

  const sortKey = searchParams.get("sort")
  const sortDir = searchParams.get("dir")
  const sort: PerformanceSort | undefined = useMemo(
    () =>
      sortKey && (sortDir === "ascend" || sortDir === "descend")
        ? { key: sortKey, dir: sortDir }
        : undefined,
    [sortKey, sortDir]
  )

  const update = useCallback(
    (patch: PerformanceUrlPatch) =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set("tab", "performance")
        const put = (key: string, value: string | null | undefined) =>
          value ? next.set(key, value) : next.delete(key)
        if (patch.grain !== undefined) {
          // A month key is not a quarter key: a new grain starts from its default period.
          if (patch.grain !== prev.get("grain")) next.delete("period")
          put("grain", patch.grain)
        }
        if (patch.period !== undefined) put("period", patch.period)
        if (patch.role !== undefined)
          put("role", patch.role === "all" ? null : patch.role)
        if (patch.projectType !== undefined) put("type", patch.projectType)
        if (patch.sort !== undefined) {
          put("sort", patch.sort?.key)
          put("dir", patch.sort?.dir)
        }
        return next
      }),
    [setSearchParams]
  )

  return { query, sort, update }
}
